import { useLocalStorage } from "@p0security/frontend-core";
import { iamShowOptions } from "@p0security/shared/assessment/constants";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  DefaultTargetNodeType,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { useLocation } from "react-router-dom";
import {
  QueryParamConfig,
  StringParam,
  UrlUpdateType,
  createEnumParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

const DEFAULT_GRAPH_VIEW_SETTINGS: Readonly<GraphViewSettings> = {
  display: "table",
} as const;

export type GraphViewSettings = {
  display: string;
};

export type Controls = {
  display: string;
  where: string;
  show: TargetNodeType;
};

type PathSettings = {
  [path: string]: GraphViewSettings;
};

const getPathKey = (pathname: string) => {
  const [_, _o, _tenant, path, ..._rest] = pathname.split("/");
  return Object.values(AppPaths).includes(path as AppPaths) ? path : "default";
};

export const useControls = () => {
  const location = useLocation();
  const pathKey = getPathKey(location.pathname);

  const [allSettings, setAllSettings] = useLocalStorage<PathSettings>(
    "graph-view-settings",
    {}
  );

  const settings = allSettings[pathKey] || DEFAULT_GRAPH_VIEW_SETTINGS;

  const [controls, setControls] = useQueryParams({
    display: withDefault(createEnumParam(["graph", "table"]), settings.display),
    where: withDefault(StringParam, ""),
    show: withDefault(
      createEnumParam(
        Object.values(iamShowOptions("item")).map((v) => v.value)
      ),
      DefaultTargetNodeType
    ) as QueryParamConfig<TargetNodeType>,
  });

  const setCombinedControls = (
    newControls: Partial<Controls>,
    updateType?: UrlUpdateType
  ) => {
    if (newControls.display !== undefined) {
      const updatedSettings = {
        ...allSettings,
        [pathKey]: { display: newControls.display },
      };
      setAllSettings(updatedSettings);
    }
    setControls(newControls, updateType);
  };

  return { controls, setControls: setCombinedControls };
};
